import { Button, DialogAwareButtonContainer, DialogAwareContentContainer, DialogContent } from "@/components";
import { FormControlLabel, Checkbox } from "@mui/material";
import type React from "react";
import { memo, type Dispatch, type SetStateAction, useState } from "react";
import { twMerge } from "tailwind-merge";

interface TransactionDialogContentProps {
    isLoading?: boolean;
    confirmText?: string;
    loadingText?: string;
    children: React.ReactNode;
    disabled?: boolean;
    disclaimer?: string;
    onClose?: () => void;
    onConfirm?: () => Promise<void>;
    className?: string;
    message?: string;
}

const Disclaimer = memo(function Disclaimer({
    disclaimer,
    checked,
    setChecked,
    disabled = false,
}: { disclaimer: string; checked: boolean; setChecked: Dispatch<SetStateAction<boolean>>; disabled?: boolean }) {
    return (
        <FormControlLabel
            control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
            label={disclaimer}
            disableTypography
            className="px-4 py-2 text-xs leading-1"
            disabled={disabled}
            autoFocus
        />
    );
});

export const TransactionDialogContent = memo(function TransactionDialogContent({
    isLoading,
    confirmText = "Confirm",
    loadingText,
    children,
    disabled,
    disclaimer,
    onConfirm,
    className,
    message,
}: TransactionDialogContentProps) {
    const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false);

    const isDisclaimerDisabled = isLoading || disabled;
    const isConfirmDisabled = isLoading || disabled || (disclaimer && !isDisclaimerChecked) || !onConfirm;

    return (
        <DialogContent className={twMerge("min-w-sm max-w-lg w-md", className)}>
            <DialogAwareContentContainer className="gap-4" isDialog>
                {children}
            </DialogAwareContentContainer>
            <DialogAwareButtonContainer className="gap-2" isDialog>
                <div className="flex flex-col">
                    {disclaimer && (
                        <Disclaimer
                            disclaimer={disclaimer}
                            setChecked={setIsDisclaimerChecked}
                            checked={isDisclaimerChecked}
                            disabled={isDisclaimerDisabled}
                        />
                    )}
                    {message && <span className="px-0.5 py-1 text-xs text-gold">{message}</span>}
                    <Button
                        onClick={onConfirm}
                        disabled={isConfirmDisabled}
                        isLoading={isLoading}
                        fullWidth
                        loadingText={loadingText}
                        size="md"
                    >
                        {confirmText}
                    </Button>
                </div>
            </DialogAwareButtonContainer>
        </DialogContent>
    );
});

Disclaimer.displayName = "Disclaimer";

TransactionDialogContent.displayName = "TransactionDialogContent";
